import { useEffect, useMemo } from 'react';
import { createSearchParams, useLocation, useMatches, useNavigate } from 'react-router-dom';

// project import
import useAuth from 'hooks/useAuth';

// types
import { GuardProps } from 'types/auth';

// utils
import useDynamicConfig from 'hooks/useDynamicConfig';

// ==============================|| AUTH GUARD ||============================== //

const AuthGuard = ({ children }: GuardProps) => {
  const { isLoggedIn, activeRole } = useAuth();
  const navigate = useNavigate();
  const { pathname, key, search } = useLocation();
  const matches = useMatches();
  const { DEFAULT_ROLE_BASED_PATH } = useDynamicConfig();

  const isAuthorized = useMemo(() => {
    const match = [...matches].reverse().find((x) => (x.handle as any)?.allowedRoles);
    const handle = match ? match.handle : undefined;
    const allowedRoles = handle ? ((handle as any).allowedRoles as number[]) : undefined;

    return activeRole?.user_role_type_id ? allowedRoles?.includes(activeRole.user_role_type_id) || false : false;
  }, [matches, activeRole?.user_role_type_id]);

  // 1️⃣ Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      const params = key === 'default' ? `?${createSearchParams({ returnUrl: pathname + search })}` : undefined;

      navigate({ pathname: '/login', search: params }, { replace: true });
    }
  }, [isLoggedIn, key, pathname, search, navigate]);

  // 2️⃣ Redirect if logged in but not authorized
  useEffect(() => {
    if (isLoggedIn && !isAuthorized && activeRole) {
      const defaultPath = DEFAULT_ROLE_BASED_PATH[activeRole.user_role_type_id];
      if (defaultPath && pathname !== defaultPath) {
        navigate({ pathname: defaultPath }, { replace: true });
      }
    }
  }, [isLoggedIn, isAuthorized, activeRole, pathname, navigate, DEFAULT_ROLE_BASED_PATH]);

  return children;
};

export default AuthGuard;
